import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import './AdminApps.css';
import { Link } from 'react-router-dom';

function AdminApps() {
  const { currentUser, isSuperAdmin } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    domain: '',
    logo_url: '',
    primary_color: '#3498db',
    secondary_color: '#2ecc71',
    is_active: true
  });
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Debug user information
  useEffect(() => {
    console.log('AdminApps - Auth Debug Info:', {
      currentUser,
      isSuperAdmin,
      userRoles: currentUser?.roles || [],
      hasToken: !!localStorage.getItem('token') || !!sessionStorage.getItem('token'),
      tokenValue: localStorage.getItem('token') || sessionStorage.getItem('token')
    });
    
    // Check headers that will be sent with API requests
    const debugHeaders = async () => {
      try {
        const headers = await apiService.getRequestHeaders();
        console.log('API Request Headers:', headers);
      } catch (err) {
        console.error('Failed to get request headers:', err);
      }
    };
    
    debugHeaders();
  }, [currentUser, isSuperAdmin]);

  // Fetch all apps on component mount
  useEffect(() => {
    if (isSuperAdmin) {
      fetchApps();
      fetchUsers();
    }
  }, [isSuperAdmin]);

  // Fetch all SaltMarsh Apps
  const fetchApps = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/apps');
      console.log('Apps response:', response);
      
      if (response && response.success && Array.isArray(response.data)) {
        setApps(response.data);
        setError(null);
      } else if (response && response.apps) {
        // Fallback for backward compatibility
        setApps(response.apps);
        setError(null);
      } else {
        console.error('Unexpected response format:', response);
        setError('Failed to load apps. Unexpected response format.');
      }
    } catch (err) {
      console.error('Failed to fetch apps:', err);
      setError('Failed to load apps. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all users
  const fetchUsers = async () => {
    try {
      // The /api/users endpoint doesn't exist in the backend
      // We need to either use a different endpoint or handle the error gracefully
      console.log('Attempting to fetch users - this endpoint may not be implemented yet');
      
      try {
        // Try to use the correct endpoint if it exists
        const response = await apiService.get('/users/all');
        setUsers(response.users || []);
      } catch (innerErr) {
        console.error('Failed to fetch users with /users/all, using mock data:', innerErr);
        // Use mock data as a fallback
        setUsers([
          { id: 1, name: 'Admin User', email: 'admin@example.com', username: 'admin' },
          // Add more mock users if needed
        ]);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  // Fetch users for a specific app
  const fetchAppUsers = async (appId) => {
    try {
      const response = await apiService.get(`/apps/${appId}/users`);
      console.log(`App users response for app ${appId}:`, response);
      
      if (response && response.success && Array.isArray(response.data)) {
        setSelectedUsers(response.data);
      } else if (response && response.users) {
        // Fallback for backward compatibility
        setSelectedUsers(response.users);
      } else {
        console.error(`Failed to fetch users for app ${appId}:`, response);
        setSelectedUsers([]);
      }
    } catch (err) {
      console.error(`Failed to fetch users for app ${appId}:`, err);
      setSelectedUsers([]);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle app selection
  const handleSelectApp = async (app) => {
    setSelectedApp(app);
    setFormMode('edit');
    setFormData({
      name: app.name,
      description: app.description,
      domain: app.domain,
      logo_url: app.logo_url || '',
      primary_color: app.primary_color || '#3498db',
      secondary_color: app.secondary_color || '#2ecc71',
      is_active: app.is_active
    });
    await fetchAppUsers(app.id);
  };

  // Reset form to create mode
  const handleNewApp = () => {
    setSelectedApp(null);
    setFormMode('create');
    setFormData({
      name: '',
      description: '',
      domain: '',
      logo_url: '',
      primary_color: '#3498db',
      secondary_color: '#2ecc71',
      is_active: true
    });
    setSelectedUsers([]);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    // First check if the user is already selected
    const isSelected = selectedUsers.some(selectedUser => {
      if (selectedUser.id === user.id) return true;
      if (selectedUser.user_id === user.id) return true;
      return false;
    });
    
    if (isSelected) return false;
    
    // Then check if the user matches the search term
    return (
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Filter apps based on search term
  const filteredApps = apps.filter(app => 
    app.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    app.domain?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Submit form to create or update an app
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      if (formMode === 'create') {
        // Create new app
        const response = await apiService.post('/apps', formData);
        console.log('Create app response:', response);
        
        if (response && response.success && response.data) {
          setApps([...apps, response.data]);
          setSelectedApp(response.data);
          setFormMode('edit');
          setError(null);
        } else if (response && response.app) {
          // Fallback for backward compatibility
          setApps([...apps, response.app]);
          setSelectedApp(response.app);
          setFormMode('edit');
          setError(null);
        } else {
          console.error('Unexpected create app response:', response);
          setError('Failed to create app. Unexpected response format.');
        }
      } else {
        // Update existing app
        const response = await apiService.put(`/apps/${selectedApp.id}`, formData);
        console.log('Update app response:', response);
        
        if (response && response.success && response.data) {
          setApps(apps.map(app => app.id === selectedApp.id ? response.data : app));
          setSelectedApp(response.data);
          setError(null);
        } else if (response && response.app) {
          // Fallback for backward compatibility
          setApps(apps.map(app => app.id === selectedApp.id ? response.app : app));
          setSelectedApp(response.app);
          setError(null);
        } else {
          console.error('Unexpected update app response:', response);
          setError('Failed to update app. Unexpected response format.');
        }
      }
    } catch (err) {
      console.error('Failed to save app:', err);
      setError('Failed to save app. Please check your inputs and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete an app
  const handleDeleteApp = async () => {
    if (!selectedApp) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedApp.name}?`)) {
      try {
        setLoading(true);
        const response = await apiService.delete(`/apps/${selectedApp.id}`);
        console.log('Delete app response:', response);
        
        if (response && (response.success || response.message)) {
          setApps(apps.filter(app => app.id !== selectedApp.id));
          handleNewApp(); // Reset form
          setError(null);
        } else {
          console.error('Unexpected delete app response:', response);
          setError('Failed to delete app. Unexpected response format.');
        }
      } catch (err) {
        console.error('Failed to delete app:', err);
        setError('Failed to delete app. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Add a user to an app
  const handleAddUser = async (userId) => {
    if (!selectedApp) return;
    
    try {
      const response = await apiService.post(`/apps/${selectedApp.id}/users`, { user_id: userId });
      console.log('Add user to app response:', response);
      
      if (response && response.success) {
        await fetchAppUsers(selectedApp.id);
      } else {
        console.error('Failed to add user to app:', response);
        setError('Failed to add user to app. Please try again.');
      }
    } catch (err) {
      console.error('Failed to add user to app:', err);
      setError('Failed to add user to app. Please try again.');
    }
  };

  // Remove a user from an app
  const handleRemoveUser = async (userId) => {
    if (!selectedApp) return;
    
    try {
      const response = await apiService.delete(`/apps/${selectedApp.id}/users/${userId}`);
      console.log('Remove user from app response:', response);
      
      if (response && response.success) {
        setSelectedUsers(selectedUsers.filter(user => user.id !== userId));
      } else {
        console.error('Failed to remove user from app:', response);
        setError('Failed to remove user from app. Please try again.');
      }
    } catch (err) {
      console.error('Failed to remove user from app:', err);
      setError('Failed to remove user from app. Please try again.');
    }
  };

  // If user is not a superadmin, show access denied message
  if (!isSuperAdmin) {
    return (
      <div className="admin-container">
        <h1>Access Denied</h1>
        <p>You do not have permission to access this page. This area is restricted to superadmins only.</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <h1>App Management</h1>
      
      {/* Admin navigation */}
      <div className="admin-nav mb-4">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <Link to="/admin" className="nav-link">Dashboard</Link>
          </li>
          <li className="nav-item">
            <Link to="/admin/apps" className="nav-link active">Apps</Link>
          </li>
          <li className="nav-item">
            <Link to="/admin/users" className="nav-link">Users</Link>
          </li>
          <li className="nav-item">
            <Link to="/admin/roles" className="nav-link">Roles</Link>
          </li>
        </ul>
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      <div className="admin-layout">
        {/* Left side - Apps list */}
        <div className="admin-sidebar">
          <div className="admin-search">
            <input
              type="text"
              placeholder="Search apps..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
          </div>
          
          <button 
            className="btn btn-primary mb-3 w-100"
            onClick={handleNewApp}
          >
            Create New App
          </button>
          
          {loading && <p>Loading apps...</p>}
          
          <div className="admin-list">
            {filteredApps.map(app => (
              <div 
                key={app.id} 
                className={`admin-list-item ${selectedApp?.id === app.id ? 'active' : ''}`}
                onClick={() => handleSelectApp(app)}
              >
                <div className="admin-list-item-name">{app.name}</div>
                <div className="admin-list-item-details">{app.domain}</div>
                <div className={`app-status ${app.is_active ? 'active' : 'inactive'}`}>
                  {app.is_active ? 'Active' : 'Inactive'}
                </div>
                <div className="admin-list-item-actions">
                  <Link 
                    to={`/apps/${app.id}/courses`} 
                    className="btn btn-sm btn-outline-primary"
                    onClick={(e) => e.stopPropagation()}
                    title="Manage Courses"
                  >
                    <i className="fas fa-graduation-cap"></i> Courses
                  </Link>
                </div>
              </div>
            ))}
            
            {filteredApps.length === 0 && !loading && (
              <p>No apps found.</p>
            )}
          </div>
        </div>
        
        {/* Right side - App form */}
        <div className="admin-content">
          <form onSubmit={handleSubmit}>
            <h2>{formMode === 'create' ? 'Create New App' : 'Edit App'}</h2>
            
            <div className="mb-3">
              <label htmlFor="name" className="form-label">App Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="description" className="form-label">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-control"
                rows="3"
              ></textarea>
            </div>
            
            <div className="mb-3">
              <label htmlFor="domain" className="form-label">Domain</label>
              <input
                type="text"
                id="domain"
                name="domain"
                value={formData.domain}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="logo_url" className="form-label">Logo URL</label>
              <input
                type="text"
                id="logo_url"
                name="logo_url"
                value={formData.logo_url}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="primary_color" className="form-label">Primary Color</label>
                <div className="input-group">
                  <input
                    type="color"
                    id="primary_color"
                    name="primary_color"
                    value={formData.primary_color}
                    onChange={handleInputChange}
                    className="form-control form-control-color"
                  />
                  <input
                    type="text"
                    aria-label="Primary color hex"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({...formData, primary_color: e.target.value})}
                    className="form-control"
                  />
                </div>
              </div>
              
              <div className="col-md-6">
                <label htmlFor="secondary_color" className="form-label">Secondary Color</label>
                <div className="input-group">
                  <input
                    type="color"
                    id="secondary_color"
                    name="secondary_color"
                    value={formData.secondary_color}
                    onChange={handleInputChange}
                    className="form-control form-control-color"
                  />
                  <input
                    type="text"
                    aria-label="Secondary color hex"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({...formData, secondary_color: e.target.value})}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="form-check-input"
              />
              <label htmlFor="is_active" className="form-check-label">Active</label>
            </div>
            
            <div className="d-flex gap-2 mb-4">
              <button type="submit" className="btn btn-primary">
                {formMode === 'create' ? 'Create App' : 'Update App'}
              </button>
              
              {formMode === 'edit' && (
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteApp}
                >
                  Delete App
                </button>
              )}
              
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={handleNewApp}
              >
                Cancel
              </button>
            </div>
          </form>
          
          {/* User management (only visible when editing an app) */}
          {selectedApp && (
            <div className="mt-4">
              <h3>App Users</h3>
              <p>Manage users associated with this app.</p>
              
              {/* Current users in the app */}
              {selectedUsers.length > 0 && (
                <div className="mb-4">
                  <h4>Current Users</h4>
                  <div className="list-group mb-3">
                    {selectedUsers.map(user => {
                      const userId = user.id || user.user_id;
                      const username = user.username || user.name || 'Unknown User';
                      const email = user.email || '';
                      
                      return (
                        <div key={userId} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{username}</strong>
                            {email && <p className="mb-0 small">{email}</p>}
                          </div>
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRemoveUser(userId)}
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Add users section */}
              <div>
                <h4>Add Users</h4>
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-control"
                  />
                </div>
                
                <div className="list-group">
                  {filteredUsers.map(user => {
                    const userId = user.id;
                    const username = user.username || user.name || 'Unknown User';
                    const email = user.email || '';
                    
                    return (
                      <div key={userId} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{username}</strong>
                          {email && <p className="mb-0 small">{email}</p>}
                        </div>
                        <button 
                          className="btn btn-sm btn-success"
                          onClick={() => handleAddUser(userId)}
                        >
                          Add
                        </button>
                      </div>
                    );
                  })}
                  
                  {filteredUsers.length === 0 && (
                    <p className="text-center py-3">No users found or all users are already added to this app.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminApps; 